import dynamic from "next/dynamic";

import { faSignInAlt, faSignOutAlt, faUser, faUserFriends } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useApi } from "~/hooks/useApi";
import { useModal } from "~/hooks/useModal";
import { WYSIWYGText } from "../WYSIWYGText";
import { useRouter } from "next/router";
import useAuth from "~/hooks/useAuth";

const BookingAssistant = dynamic(() => import("./BookingAssistant"));

const EventPreviewWrapper = styled.div`
    span {
        border: 1px solid ${(p) => p.theme.borderColor};
        border-radius: ${(p) => p.theme.borderRadius};
        padding: 0.5rem;
        margin: 0.25rem;
        font-size: 1.1rem;

        text-align: center;
        box-shadow: ${(p) => p.theme.shadows.sm};
    }

    .event-header  {
        display: flex;
        align-items: flex-start;

        h2 {
            flex: 1 1 auto;
        }

        span {
            font-weight: ${(p) => p.theme.fontWeightMedium};
            white-space: nowrap;
        }
    }

    .event-subheader {
        display: flex;
        justify-content: space-around;
        align-items: center;
        margin: 0.5rem 0;

        svg {
            margin-right: 0.75rem;
        }

        span {
            flex: 1 1 auto;
        }
    }
`;

const EventPreview = ({ eventSlug, event: _event = false, showButtons = false, ...props }) => {
    const api = useApi();
    const [setModal, reset] = useModal();
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(!_event);
    const { isLoading: authIsLoading } = useAuth();
    const [actionIsLoading, setActionIsLoading] = useState(false);
    const [error, setError] = useState();
    const [event, setEvent] = useState(_event);

    const fetchEvent = () => {
        if (!_event && eventSlug && !authIsLoading)
            api({
                url: "events/" + eventSlug,
                onSuccess: ({ data }) => setEvent(data),
                onFinish: () => setIsLoading(false),
                onFailure: (data) => setError(data),
                muted: true,
            });
    };

    const handleBooking = () =>
        setModal({
            title: "Event buchen",
            content: <BookingAssistant event={event} />,
            show: true,
            size: "lg",
        });

    const handleDelete = () => {
        setActionIsLoading(true);
        api({
            url: "events/" + eventSlug,
            method: "DELETE",
            onSuccess: () => router.reload(),
            onFinish: () => setActionIsLoading(false),
            onFailure: (data) => setError(data),
            muted: true,
        });
    };

    // ! implement update method, pay attention to already created bookings
    const handleUpdate = () => {};

    const handleCancelBooking = () => {
        setActionIsLoading(true);
        api({
            url: "bookings/" + event.bookingId,
            method: "DELETE",
            onSuccess: () => router.reload(),
            onFinish: () => setActionIsLoading(false),
            onFailure: (data) => setError(data),
            muted: true,
        });
    };

    useEffect(fetchEvent, [eventSlug, _event, authIsLoading]);

    return (
        <>
            {isLoading ? (
                "Lade ..."
            ) : error ? (
                "Es ist ein Fehler aufgetreten."
            ) : (
                <EventPreviewWrapper {...props}>
                    <div className="event-header">
                        <h2>{event.title}</h2>
                        {event.price && <span>{event.price} €</span>}
                    </div>
                    <div className="event-subheader">
                        <span>
                            <FontAwesomeIcon icon={faSignOutAlt} />
                            {moment(event.start).format("D.M.YY, HH:mm")}
                        </span>
                        <span>
                            <FontAwesomeIcon icon={faSignInAlt} />
                            {moment(event.end).format("D.MM.YY, HH:mm")}
                        </span>
                    </div>
                    <div className="event-subheader">
                        <span>
                            <FontAwesomeIcon icon={faUserFriends} />
                            {event.subscriber + " / " + event.maxMember}
                        </span>
                        <span>
                            <FontAwesomeIcon icon={faUser} />
                            {event.creator?.name}
                        </span>
                    </div>
                    <div className="event-subheader"></div>
                    <WYSIWYGText>{event.description}</WYSIWYGText>
                    {showButtons && (
                        <>
                            <div className="btn-group w-100 mb-2">
                                {event.can.delete && (
                                    <button className="btn btn-danger" onClick={handleDelete}>
                                        Löschen
                                    </button>
                                )}
                                {/* {event.can.update && (
                            <button className="btn btn-warning" onClick={handleUpdate}>
                                Bearbeiten
                            </button>
                        )} */}
                            </div>
                            {event.can.cancel ? (
                                <button className="btn btn-danger btn-block" onClick={handleCancelBooking}>
                                    Stornieren
                                </button>
                            ) : (
                                event.can.book && (
                                    <button className="btn btn-success btn-block" onClick={handleBooking}>
                                        Weiter zur Buchung
                                    </button>
                                )
                            )}
                        </>
                    )}
                </EventPreviewWrapper>
            )}
        </>
    );
};

export default EventPreview;
